// A project's figures beside the current state's: how much a figure went up or down, and whether that's better. The
// analysis views show it next to each figure when a project is looked at

export interface Measure {
    amount: number;
    better: 'up' | 'down' | 'none';         // which way is an improvement; 'none' when neither is
    unit: 'money' | 'hours' | 'percent' | 'count' | 'number';
}

export interface Change {
    text: string;                           // "▲ $40K (4%)"
    tone: 'good' | 'bad' | 'flat';
}

export const measure = (amount: number, better: Measure['better'], unit: Measure['unit']): Measure => ({ amount, better, unit });

// the change from the current state's figure to the project's. A figure the current state doesn't have is new; one
// in another unit can't be compared
export const changeFrom = (before: Measure | undefined, after: Measure | undefined, format: (amount: number, unit: Measure['unit']) => string): Change | undefined => {
    if (!after) return undefined;
    if (!before) return { text: 'new', tone: 'flat' };
    if (before.unit !== after.unit) return undefined;
    const diff = after.amount - before.amount;
    // percentages are shown whole, so a change smaller than a point doesn't show
    if (after.unit === 'percent' ? Math.round(diff) === 0 : Math.abs(diff) < 0.5) return { text: 'no change', tone: 'flat' };
    const arrow = diff < 0 ? '▼' : '▲';
    const absolute = format(Math.abs(diff), after.unit);
    const share = after.unit !== 'percent' && before.amount !== 0 ? ` (${Math.round(Math.abs(diff) / Math.abs(before.amount) * 100)}%)` : '';
    const better = after.better === 'none' ? undefined : after.better === 'up' ? diff > 0 : diff < 0;
    return { text: `${arrow} ${absolute}${share}`, tone: better === undefined ? 'flat' : better ? 'good' : 'bad' };
};

// the usual way to write a change in each unit
export const changeFormat = (money: (v: number) => string, number: (v: number) => string) => (amount: number, unit: Measure['unit']): string => {
    switch (unit) {
        case 'money': return money(amount);
        case 'hours': return `${number(amount)} h`;
        case 'percent': return `${Math.round(amount)} pts`;
        default: return number(amount);
    }
};
